import {
  APIDashboardFilterExpression,
  APILinkedDashboardFilterParameter,
  ArdoqId,
} from '@ardoq/api-types';
import { Widgets$State } from './types';
import { isEmpty, omit } from 'lodash';
import { reducer } from '@ardoq/rxbeach';
import { withUpdatedFilterValue } from 'dashboard/dashboardFilterValuesOperations';
import {
  autoLinkDashboardFilterToWidgets,
  createDashboardFilterParameter,
  removeDashboardFilterParameter,
  reorderDashboardFilterParameters,
  updateDashboardFilterValues,
  unlinkWidgetWithDashboardFilter,
  linkWidgetWithDashboardFilter,
  widgetDashboardFiltersAppliedOnDrawerSave,
  WidgetDashboardFiltersAppliedPayload,
  clearAllDashboardFilterValuesClicked,
  updateDashboardFilterParameterName,
} from './actions';
import { getSelectedChartWidget } from '../utils';
import { getUpdatedWidgets } from './stateUtils';
import { updateState } from '@ardoq/common-helpers';

const handleCreateDashboardFilterParameter = (
  state: Widgets$State,
  { name, id }: { name: string; id: string }
): Widgets$State => {
  return {
    ...state,
    globalFilterParameters: [
      ...state.globalFilterParameters,
      {
        name,
        id,
        type: undefined,
        fieldApiName: undefined,
      },
    ],
  };
};

const handleUpdateDashboardFilterParameterName = (
  state: Widgets$State,
  { parameterId, newName }: { parameterId: string; newName: string }
): Widgets$State => {
  return {
    ...state,
    globalFilterParameters: state.globalFilterParameters.map(parameter => {
      if (parameter.id === parameterId) {
        return {
          ...parameter,
          name: newName,
        };
      }
      return parameter;
    }),
  };
};

export const handleReorderDashboardFilterParameters = (
  state: Widgets$State,
  { fromIndex, toIndex }: { fromIndex: number; toIndex: number }
): Widgets$State => {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= state.globalFilterParameters.length ||
    toIndex >= state.globalFilterParameters.length
  ) {
    return state;
  }
  const newParameters = [...state.globalFilterParameters];
  const [moved] = newParameters.splice(fromIndex, 1);
  newParameters.splice(toIndex, 0, moved);
  return {
    ...state,
    globalFilterParameters: newParameters,
  };
};

const handleRemoveDashboardFilterParameter = (
  state: Widgets$State,
  id: string
): Widgets$State => {
  return {
    ...state,
    globalFilterValues: omit(state.globalFilterValues, [id]),
    globalFilterParameters: state.globalFilterParameters.filter(
      param => param.id !== id
    ),
    widgets: state.widgets.map(widget => {
      if (!('globalFilterBindings' in widget)) return widget;

      const newGlobalFilterBindings = Object.fromEntries(
        Object.entries(widget.globalFilterBindings || {}).filter(
          ([_fieldKey, parameterId]) => parameterId !== id
        )
      );
      return {
        ...widget,
        globalFilterBindings: isEmpty(newGlobalFilterBindings)
          ? undefined
          : newGlobalFilterBindings,
      };
    }),
  };
};

const handleUpdateDashboardFilterValues = (
  state: Widgets$State,
  {
    parameterId,
    values,
  }: { parameterId: string; values: APIDashboardFilterExpression[] | null }
): Widgets$State => ({
  ...state,
  globalFilterValues: withUpdatedFilterValue({
    globalFilterValues: state.globalFilterValues,
    parameterId,
    values,
  }),
});

const handleUnlinkWidgetWithDashboardFilter = (
  state: Widgets$State,
  {
    widgetId,
    dashboardFilterParameterId,
  }: { widgetId: ArdoqId; dashboardFilterParameterId: ArdoqId }
): Widgets$State => {
  const selectedWidget = getSelectedChartWidget(state.widgets, widgetId);

  if (!selectedWidget.globalFilterBindings) {
    return state;
  }

  const newGlobalFilterBindings = Object.fromEntries(
    Object.entries(selectedWidget.globalFilterBindings).filter(
      ([, parameterId]) => parameterId !== dashboardFilterParameterId
    )
  );
  const widgets = getUpdatedWidgets(state.widgets, [
    {
      _id: widgetId,
      globalFilterBindings: isEmpty(newGlobalFilterBindings)
        ? undefined
        : newGlobalFilterBindings,
    },
  ]);

  const shouldClearPending =
    state.pendingConditionSelection?.widgetId === widgetId &&
    state.pendingConditionSelection.conflictType === 'dashboardFilter' &&
    !(state.pendingConditionSelection.columnKey in newGlobalFilterBindings);

  return {
    ...state,
    widgets,
    selectedWidgetId: null,
    ...(shouldClearPending && { pendingConditionSelection: undefined }),
  };
};

const handleLinkWidgetWithDashboardFilter = (
  state: Widgets$State,
  {
    widgetId,
    dashboardFilterParameterId,
  }: { widgetId: ArdoqId; dashboardFilterParameterId: ArdoqId }
): Widgets$State => {
  const selectedWidget = getSelectedChartWidget(state.widgets, widgetId);

  const dashboardFilterParameter = state.globalFilterParameters.find(
    parameter => parameter.id === dashboardFilterParameterId
  );

  if (!dashboardFilterParameter?.fieldApiName) {
    return state;
  }

  const newGlobalFilterBindings = {
    ...selectedWidget.globalFilterBindings,
    [dashboardFilterParameter.fieldApiName]: dashboardFilterParameterId,
  };
  const widgets = getUpdatedWidgets(state.widgets, [
    {
      _id: widgetId,
      globalFilterBindings: newGlobalFilterBindings,
    },
  ]);

  return {
    ...state,
    widgets,
    selectedWidgetId: null,
  };
};

const handleAutoLinkDashboardFilterToWidgets = (
  state: Widgets$State,
  {
    dashboardFilterParameter,
    widgetIdsToLink,
  }: {
    dashboardFilterParameter: APILinkedDashboardFilterParameter;
    widgetIdsToLink: ArdoqId[];
  }
) => {
  const selectedWidgets = widgetIdsToLink.map(id =>
    getSelectedChartWidget(state.widgets, id)
  );

  const widgets = getUpdatedWidgets(
    state.widgets,
    selectedWidgets.map(widget => ({
      ...widget,
      globalFilterBindings: {
        ...widget.globalFilterBindings,
        [dashboardFilterParameter.fieldApiName]: dashboardFilterParameter.id,
      },
    }))
  );

  return {
    ...state,
    widgets,
  };
};

const handleWidgetDashboardFiltersApplied = (
  state: Widgets$State,
  {
    widgetId,
    dashboardFilterBindings,
    linkModeDashboardFilterParameter,
  }: WidgetDashboardFiltersAppliedPayload
): Widgets$State => {
  const updatedWidgets = getUpdatedWidgets(state.widgets, [
    {
      _id: widgetId,
      globalFilterBindings: dashboardFilterBindings,
    },
  ]);

  return {
    ...state,
    widgets: updatedWidgets,
    globalFilterParameters: state.globalFilterParameters.map(parameter => {
      if (
        linkModeDashboardFilterParameter &&
        parameter.id === linkModeDashboardFilterParameter.id &&
        !parameter.fieldApiName &&
        dashboardFilterBindings &&
        Object.values(dashboardFilterBindings).includes(parameter.id)
      ) {
        return linkModeDashboardFilterParameter;
      }

      return parameter;
    }),
  };
};

export const dashboardFiltersReducers = [
  reducer(createDashboardFilterParameter, handleCreateDashboardFilterParameter),
  reducer(
    updateDashboardFilterParameterName,
    handleUpdateDashboardFilterParameterName
  ),
  reducer(removeDashboardFilterParameter, handleRemoveDashboardFilterParameter),
  reducer(
    reorderDashboardFilterParameters,
    handleReorderDashboardFilterParameters
  ),
  reducer(updateDashboardFilterValues, handleUpdateDashboardFilterValues),
  reducer(
    unlinkWidgetWithDashboardFilter,
    handleUnlinkWidgetWithDashboardFilter
  ),
  reducer(linkWidgetWithDashboardFilter, handleLinkWidgetWithDashboardFilter),
  reducer(
    widgetDashboardFiltersAppliedOnDrawerSave,
    handleWidgetDashboardFiltersApplied
  ),
  reducer(
    clearAllDashboardFilterValuesClicked,
    updateState<Widgets$State, Widgets$State>({ globalFilterValues: {} })
  ),
  reducer(
    autoLinkDashboardFilterToWidgets,
    handleAutoLinkDashboardFilterToWidgets
  ),
];
